import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import IconButton from 'fe-design-base/molecules/IconButton';
import Tabs from 'fe-design-base/molecules/Tabs';

import {
  fetchNotifications,
  toggleNotificationsShown,
} from 'actions/notifications';

import { buildGetDrawerOpen } from 'selectors/drawers';
import {
  getAlertsItems,
  getHasMoreAlerts,
  getHasMoreRequests,
  getIsFetchingNotifications,
  getRequestsItems,
} from 'selectors/notifications';
import { getCurrentUserIsManagerial } from 'selectors/session';

import { NOTIFICATIONS_DRAWER_KEY } from 'features/drawers/keys';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';

import { EVENT_ACTIONS, trackNotificationEvents } from '../tracking';

import NotificationsList from './NotificationsList.jsx';

const { cxEl } = cxHelpers('NotificationView');

const NAV_HEADERS = [
  {
    value: 'requests',
    label: toI18n('notifications.requests.title'),
  },
  {
    value: 'alerts',
    label: toI18n('notifications.alerts.title'),
  },
];

const EMPLOYEE_NAV_HEADERS = [NAV_HEADERS[0]];

const DRAWER_CONTAINER_STYLE = {
  boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
  width: '390px',
};

const NotificationsView = () => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [tabValue, setTabValue] = useState<string>('requests');
  const isManagerial = useSelector(getCurrentUserIsManagerial);

  const isOpen = useSelector(buildGetDrawerOpen(NOTIFICATIONS_DRAWER_KEY));
  const requestsItems = useSelector(getRequestsItems);
  const alertsItems = useSelector(getAlertsItems);
  const isFetching = useSelector(getIsFetchingNotifications);
  const hasMoreRequests = useSelector(getHasMoreRequests);
  const hasMoreAlerts = useSelector(getHasMoreAlerts);

  useEffect(() => {
    if (!isManagerial && tabValue !== 'requests') {
      setTabValue('requests');
    }
  }, [isManagerial, tabValue]);

  const handleClose = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.CLOSE_CLICKED,
    });
    dispatch(toggleNotificationsShown(false));
  }, [dispatch]);

  const handleAlertsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.ALERTS_CLICKED,
    });
  }, []);

  const handleRequestsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.REQUESTS_CLICKED,
    });
  }, []);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string | number) => {
      setTabValue(newValue as string);
      const handleTabClick =
        newValue === toI18n('notifications.requests.title')
          ? handleRequestsTabClick
          : handleAlertsTabClick;
      handleTabClick();
    },
    [handleRequestsTabClick, handleAlertsTabClick]
  );

  const getScrollContainer = useCallback(() => scrollContainerRef.current, []);

  const getHeaders = useCallback(
    () =>
      (isManagerial ? NAV_HEADERS : EMPLOYEE_NAV_HEADERS).map(content => {
        const collection =
          content.label === toI18n('notifications.requests.title')
            ? requestsItems
            : alertsItems;

        return {
          value: content.value,
          label: content.label,
          badgeText: collection.size > 0 ? collection.size : undefined,
          badgeVariant: 'loud' as const,
        };
      }),
    [isManagerial, requestsItems, alertsItems]
  );

  const handleFetchNotifications = useCallback(
    (params: any) => {
      if (params?.type === 'alerts' && !isManagerial) return;
      dispatch(fetchNotifications(params));
    },
    [dispatch, isManagerial]
  );

  const renderNotificationsList = useCallback(
    (type: string, items: any) => (
      <Box key={type}>
        <NotificationsList
          notificationType={type}
          notificationsItems={items}
          fetchNotifications={handleFetchNotifications}
          isFetching={isFetching}
          hasMore={type === 'requests' ? hasMoreRequests : hasMoreAlerts}
          getScrollContainer={getScrollContainer}
          onClose={handleClose}
        />
      </Box>
    ),
    [
      isFetching,
      hasMoreRequests,
      hasMoreAlerts,
      getScrollContainer,
      handleClose,
      handleFetchNotifications,
    ]
  );

  return (
    <Drawer
      containerStyle={DRAWER_CONTAINER_STYLE}
      open={isOpen}
      className={cxEl('')}
    >
      <Box column maxh="100%" pr={16}>
        <Box row alignItemsCenter gap={8}>
          <Box grow={1}>
            <Tabs
              noPadding
              onChange={handleTabChange}
              tabValue={tabValue}
              options={getHeaders()}
            />
          </Box>
          <div>
            <IconButton
              onClick={handleClose}
              icon="Close"
              className={cxEl('close-button')}
              size="small"
            />
          </div>
        </Box>
        <Box scrollY w={390}>
          {tabValue === 'requests' &&
            renderNotificationsList('requests', requestsItems)}
          {isManagerial &&
            tabValue === 'alerts' &&
            renderNotificationsList('alerts', alertsItems)}
        </Box>
      </Box>
    </Drawer>
  );
};

export default NotificationsView;
